import React, { useRef, useEffect, useState } from "react";
import { BUSINESS_NAME, BUSINESS_SHORT_NAME } from "@/core/constants/business";
import {
  animateText,
  animateImage,
  fadeInImage,
  cleanupScrollTriggers,
} from "./gsap";

const AboutDispensary: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const imgElementRef = useRef<HTMLImageElement>(null);

  // Image loading states following the standardized pattern
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Image load handler following the standardized pattern
  const handleImageLoad = (img: HTMLImageElement) => {
    console.log("✅ AboutDispensary image loaded successfully");
    setImageLoaded(true);

    // Apply GSAP fade-in animation
    fadeInImage(img, 0);
  };

  // Image error handler
  const handleImageError = () => {
    console.error("❌ Failed to load AboutDispensary image");
    setImageError(true);
  };

  useEffect(() => {
    // Apply animations with a small timeout to ensure DOM is ready
    const animationTimeout = setTimeout(() => {
      if (sectionRef.current && textRef.current && imageRef.current) {
        // Animate the text content
        animateText(
          { current: textRef.current },
          { current: sectionRef.current }
        );

        // Animate the image with a slight delay
        animateImage(
          { current: imageRef.current },
          { current: sectionRef.current }
        );
      }
    }, 100);

    // Cleanup animations and timeout on unmount
    return () => {
      clearTimeout(animationTimeout);
      cleanupScrollTriggers();
    };
  }, []);

  return (
    <div ref={sectionRef} className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row items-center gap-12">
          <div ref={textRef} className="md:w-1/2">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              {BUSINESS_SHORT_NAME}
            </h2>
            <p className="text-gray-700 mb-4">
              {BUSINESS_NAME} is a marijuana dispensary with two locations in
              Washington DC and Baltimore Maryland. {BUSINESS_NAME}, also known
              as {BUSINESS_SHORT_NAME}, opened in 2019 in Washington DC.{" "}
              {BUSINESS_SHORT_NAME} opened their second location in Baltimore
              Maryland in 2023.
            </p>
            <p className="text-gray-700 mb-4">
              Founded by CEO, Lonny the Street Lawyer, {BUSINESS_SHORT_NAME} is
              known for quality cannabis strains, recreational weed, prerolls,
              roll ups, edibles, vapes, carts, and events. No medical card
              required – come into our dispensaries. We're open Monday to Friday
              from 10AM to 10PM.
            </p>
            <p className="text-gray-700 mb-4">
              Immerse yourself in the essence of cannabis culture and discover
              the best of DC's cannabis scene.
            </p>
            <p className="text-gray-700 font-semibold">
              Come to Street Lawyer Services Dispensary for your next cannabis
              experience.
            </p>
          </div>

          <div ref={imageRef} className="md:w-1/2">
            <div className="rounded-lg overflow-hidden shadow-xl relative">
              {/* Loading indicator */}
              {!imageLoaded && !imageError && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 min-h-[300px]">
                  <div className="w-10 h-10 border-4 border-cannabis-green-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}

              {/* Error fallback */}
              {imageError && (
                <div className="min-h-[300px] flex items-center justify-center bg-gray-100">
                  <div className="text-center p-8">
                    <svg
                      className="w-12 h-12 text-gray-400 mb-4 mx-auto"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    <p className="font-medium text-gray-700">
                      Dispensary Image
                    </p>
                  </div>
                </div>
              )}

              {/* Image with optimized loading - Fixed fetchPriority */}
              {!imageError && (
                <picture>
                  <source
                    srcSet="/images/weed-jar-and-street-lawyer-services-logo.webp"
                    type="image/webp"
                  />
                  <img
                    ref={imgElementRef}
                    src="/images/weed-jar-and-street-lawyer-services-logo.jpg"
                    alt="Premium cannabis products at Street Lawyer Services dispensary"
                    className="w-full h-auto"
                    style={{ opacity: 0 }} // Start hidden for smooth fade-in
                    loading="lazy"
                    decoding="async"
                    onLoad={(e) =>
                      handleImageLoad(e.target as HTMLImageElement)
                    }
                    onError={handleImageError}
                  />
                </picture>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-2 italic text-center">
              Premium cannabis products at our Washington DC dispensary
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutDispensary;
